import algoliasearch from "algoliasearch/lite";
(function () {
  "use strict";

  // Get defaults from CampsUtils
  const defaults = window.CampsUtils?.defaults || {};

  const CampsNearYouBrand = {
    // Configuration (uses CampsUtils.defaults)
    config: {
      displayCount: defaults.displayCount || 8,
      algoliaAppId: defaults.algoliaAppId,
      algoliaSearchKey: defaults.algoliaSearchKey,
      algoliaIndex: defaults.algoliaIndex,
      cacheKey: defaults.cacheKey,
      cacheDuration: defaults.cacheDuration,
      searchRadius: 500 * 1609, // 500 miles in meters
    },

    // DOM elements
    elements: {
      container: null,
      list: null,
      title: null,
      description: null,
      titleText: null,
      cityZipPrefix: null,
      cityZip: null,
      viewAllLink: null,
    },

    // Brand slug from data attribute
    brandSlug: null,

    // Camp codes from data attribute (curated selection from Craft)
    campCodes: [],

    // Algolia index
    index: null,

    /**
     * Initialize the component
     */
    init: function () {
      this.elements.container = document.querySelector(
        '.rd-camps-near-you[data-page="brand"]',
      );
      if (!this.elements.container) return;

      // Get brandSlug from data attribute
      this.brandSlug = this.elements.container.dataset.brand;
      if (!this.brandSlug) {
        console.error(
          "[CampsNearYouBrand] ERROR: brandSlug not found in data-brand attribute",
        );
        return;
      }

      // Get curated camp codes from data attribute
      try {
        this.campCodes = JSON.parse(
          this.elements.container.dataset.campCodes || "[]",
        );
      } catch (e) {
        this.campCodes = [];
      }

      this.elements.list = this.elements.container.querySelector(
        ".rd-camps-near-you__list",
      );
      this.elements.title = this.elements.container.querySelector(
        ".rd-camps-near-you__title",
      );
      this.elements.description = this.elements.container.querySelector(
        ".rd-camps-near-you__description",
      );
      this.elements.titleText =
        this.elements.container.querySelector("[data-title-text]");
      this.elements.cityZipPrefix = this.elements.container.querySelector(
        "[data-city-zip-prefix]",
      );
      this.elements.cityZip =
        this.elements.container.querySelector("[data-city-zip]");
      this.elements.viewAllLink = this.elements.container.querySelector(
        ".rd-camps-near-you__view-all",
      );

      console.log(
        `[CampsNearYouBrand] FLOW: Initialized for brand: ${this.brandSlug} with ${this.campCodes.length} curated camps`,
      );

      this.observeAndLoad();
    },

    /**
     * Defer Algolia request until the section enters the viewport.
     * Saves Algolia operations for users who never scroll to this block.
     */
    observeAndLoad: function () {
      if (!("IntersectionObserver" in window)) {
        this.waitForAlgoliaAndLoad();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              observer.disconnect();
              this.waitForAlgoliaAndLoad();
              break;
            }
          }
        },
        { rootMargin: "200px 0px", threshold: 0 },
      );

      observer.observe(this.elements.container);
    },

    /**
     * Wait for Algolia library to load, then initialize
     */
    waitForAlgoliaAndLoad: function () {
      const maxAttempts = 50; // 5 seconds max
      let attempts = 0;

      const checkAlgolia = () => {
        if (typeof algoliasearch !== "undefined") {
          const client = algoliasearch(
            this.config.algoliaAppId,
            this.config.algoliaSearchKey,
          );
          this.index = client.initIndex(this.config.algoliaIndex);
          this.loadCamps();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkAlgolia, 100);
        } else {
          console.error(
            "[CampsNearYouBrand] ERROR: Algolia library failed to load after 5 seconds",
          );
        }
      };

      checkAlgolia();
    },

    skeletonToggle: function (show) {
      if (this.elements.title) {
        this.elements.title.classList.toggle("rd-skeleton", show);
      }
      if (this.elements.description) {
        this.elements.description.classList.toggle("rd-skeleton", show);
      }
    },

    /**
     * Main function to load camps.
     *
     * Skeletons are shown in the template until this function replaces them
     * with real cards from Algolia.
     *
     * Flow:
     *   1. Non-Nike brand → curated-by-codes request, render those.
     *   2. Nike brand → IP lookup.
     *      - US → geo-sorted request (500mi).
     *        * ≥1 hit → render geo-sorted + update title.
     *        * else → curated-by-codes request, render those.
     *      - Non-US → curated-by-codes request, render those.
     * 1 Algolia request in the common path; 2 only for Nike US without hits.
     */
    loadCamps: async function () {
      let campsToRender = [];
      let showDistanceFlag = false;
      try {
        const isNike = this.brandSlug?.toLowerCase() === "nike";

        if (isNike) {
          const location = await window.CampsUtils?.getUserLocationByIP({
            cacheKey: this.config.cacheKey,
            cacheDuration: this.config.cacheDuration,
          });

          if (location.isUS) {
            console.log(
              "[CampsNearYouBrand] FLOW: US Nike user → fetching geo-sorted",
            );
            const hits = await this.fetchCampsByBrand(location);

            if (hits && hits.length > 0) {
              this.updateTitleForUS(location);
              this.updateViewAllLink(location);
              this.renderCamps(hits, true);
              return;
            }

            console.log(
              "[CampsNearYouBrand] FLOW: Geo returned 0 → fetching curated",
            );
          } else {
            console.log(
              "[CampsNearYouBrand] FLOW: Non-US Nike user → fetching curated",
            );
          }
        } else {
          console.log(
            `[CampsNearYouBrand] FLOW: Non-Nike brand (${this.brandSlug}) → fetching curated`,
          );
        }

        if (this.campCodes.length > 0) {
          const curatedCamps = await window.CampsUtils?.fetchCampsByCodes(
            this.index,
            this.campCodes,
          );
          if (curatedCamps && curatedCamps.length > 0) {
            this.renderCamps(curatedCamps, false);
          }
        }
      } catch (error) {
        console.error("[CampsNearYouBrand] ERROR:", error);
      } finally {
        campsToRender.length > 0 &&
          this.renderCamps(campsToRender, showDistanceFlag);
        this.skeletonToggle(false);
      }
    },

    /**
     * Convert brand slug to brand name for Algolia filter
     * Example: "nbc-camps" -> "NBC Camps"
     * @param {string} slug - Brand slug (lowercase, hyphenated)
     * @returns {string} - Brand name (Title Case)
     */
    slugToBrandName: function (slug) {
      if (!slug) return "";

      return slug
        .split("-")
        .map((word) => {
          // Handle acronyms (all uppercase if 2-4 chars)
          if (word.length >= 2 && word.length <= 4) {
            return word.toUpperCase();
          }
          // Otherwise capitalize first letter
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
    },

    /**
     * Fetch camps for specific brand with 500 mile radius
     * Algolia will return camps sorted by distance from user
     * @param {Object} coords - User coordinates
     */
    fetchCampsByBrand: async function (coords) {
      const brandName = this.slugToBrandName(this.brandSlug);

      const { hits } = await this.index.search("", {
        aroundLatLng: `${coords.lat},${coords.lng}`,
        aroundRadius: this.config.searchRadius, // 500 miles
        hitsPerPage: this.config.displayCount,
        filters: `brands:"${brandName}"`,
        getRankingInfo: true,
      });
      return hits;
    },

    /**
     * Update title for US users
     * Changes from "TOP NIKE CAMPS" to "Nike camps near [City, ST]"
     * @param {Object} location - User location data
     */
    updateTitleForUS: function (location) {
      const locationText =
        location.userLocation || `${location.city}, ${location.regionCode}`;

      // Hide the default title text
      if (this.elements.titleText) {
        this.elements.titleText.classList.add("hidden");
      }

      // Show and populate the location elements
      if (this.elements.cityZipPrefix) {
        this.elements.cityZipPrefix.textContent = "near";
        this.elements.cityZipPrefix.classList.remove("hidden");
      }

      if (this.elements.cityZip) {
        this.elements.cityZip.textContent = locationText;
        this.elements.cityZip.href = `/search/results?q=${encodeURIComponent(
          locationText,
        )}&brand=${this.brandSlug}`;
        this.elements.cityZip.classList.remove("hidden");
      }

      // Update the title to show brand name before "near"
      if (this.elements.title && this.elements.titleText) {
        const brandName = this.slugToBrandName(this.brandSlug);
        // Insert brand name before prefix
        const brandSpan = document.createElement("span");
        brandSpan.textContent = `${brandName} camps `;
        this.elements.title.insertBefore(
          brandSpan,
          this.elements.cityZipPrefix,
        );
      }
    },

    /**
     * Render camp cards to DOM
     * @param {Array} camps - Array of camp hits from Algolia
     * @param {boolean} showDistanceFlag - Whether distance should be shown (US users only)
     */
    renderCamps: function (camps, showDistanceFlag) {
      if (!this.elements.list) return;

      if (camps.length === 0) {
        this.handleNoCamps();
        return;
      }

      const html = camps
        .map((camp) => {
          // Show distance whenever Algolia returned a number (incl. 0.0 mi);
          // hide only when geo data is absent.
          const showDistance =
            showDistanceFlag &&
            typeof camp._rankingInfo?.geoDistance === "number";
          const showSessions = true;
          return window.CampsUtils?.renderCampCardHtml(camp, {
            showDistance,
            showSessions,
          });
        })
        .join("");

      this.elements.list.innerHTML = html;
      pushGTMViewItemList(camps, false, null);
      selectItemTracking(camps, null);
    },

    /**
     * Handle case when no camps found for this brand
     */
    handleNoCamps: function () {
      if (this.elements.list) {
        this.elements.list.innerHTML =
          '<li class="rd-camps-near-you__no-results">No camps found for this brand.</li>';
      }
    },

    /**
     * Update "View All" link with location parameter for US users
     * @param {Object} coords - User coordinates and location data
     */
    updateViewAllLink: function (coords) {
      if (!this.elements.viewAllLink) return;

      const locationParam =
        coords.userLocation || `${coords.city}, ${coords.regionCode}`;
      const params = new URLSearchParams();
      params.set("q", locationParam);
      params.set("brand", this.brandSlug);

      this.elements.viewAllLink.href = `/search/results?${params.toString()}`;
    },
  };

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () =>
      CampsNearYouBrand.init(),
    );
  } else {
    CampsNearYouBrand.init();
  }
})();
